'use client'

import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { MdArrowBack, MdSave, MdWarning } from 'react-icons/md'
import { useAuth } from '@/store/hooks'
import {
    createGloss,
    updateGloss,
    getGloss,
    CreateGlossData,
    UpdateGlossData, GlossType, GLOSS_TYPE_LABELS
} from '@/lib/api/billing/gloss-api'
import { getHealthPlans, HealthPlan } from '@/lib/api/medical/health-plan-api'
import { format } from 'date-fns'

interface FormData {
    healthPlanId: string
    guideId: string
    guideNumber: string
    guideItemId: string
    procedureCode: string
    procedureName: string
    glossType: GlossType
    glossCode: string
    glossReason: string
    originalValue: string
    glossedValue: string
    glossDate: string
    notes: string
}

export default function GlossFormPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const isEditing = !!id

    const [loading, setLoading] = useState(false)
    const [loadingData, setLoadingData] = useState(false)
    const [healthPlans, setHealthPlans] = useState<HealthPlan[]>([])

    const [formData, setFormData] = useState<FormData>({
        healthPlanId: '',
        guideId: '',
        guideNumber: '',
        guideItemId: '',
        procedureCode: '',
        procedureName: '',
        glossType: 'GLOSS_ADMINISTRATIVE',
        glossCode: '',
        glossReason: '',
        originalValue: '',
        glossedValue: '',
        glossDate: format(new Date(), 'yyyy-MM-dd'),
        notes: '',
    })

    useEffect(() => {
        if (user?.companyId) {
            loadHealthPlans()
            if (isEditing && id) {
                loadGloss()
            }
        }
    }, [id, isEditing, user?.companyId])

    const loadHealthPlans = async () => {
        if (!user?.companyId) return
        try {
            const response = await getHealthPlans(user.organizationId)
            if (response.success) {
                setHealthPlans(response.result || [])
            }
        } catch (error) {
            console.error('Erro ao carregar convênios:', error)
        }
    }

    const loadGloss = async () => {
        if (!id) return

        setLoadingData(true)
        try {
            const response = await getGloss(parseInt(id, 10))
            if (response.success && response.result) {
                const gloss = response.result
                setFormData({
                    healthPlanId: gloss.healthPlanId?.toString() || '',
                    guideId: gloss.guideId?.toString() || '',
                    guideNumber: gloss.guide?.guideNumber || '',
                    guideItemId: gloss.itemId?.toString() || '',
                    procedureCode: gloss.item?.procedureCode || '',
                    procedureName: gloss.item?.procedureName || '',
                    glossType: gloss.type || 'GLOSS_ADMINISTRATIVE',
                    glossCode: gloss.glossCode || '',
                    glossReason: gloss.glossDescription || '',
                    originalValue: gloss.originalValue?.toString() || '',
                    glossedValue: gloss.glossValue?.toString() || '',
                    glossDate: gloss.createdAt
                        ? format(new Date(gloss.createdAt), 'yyyy-MM-dd')
                        : '',
                    notes: gloss.notes || '',
                })
            } else {
                toast.error(response.error || 'Erro ao carregar glosa')
                navigate('/billing/glosses')
            }
        } catch (error) {
            toast.error('Erro ao carregar glosa')
            navigate('/billing/glosses')
        } finally {
            setLoadingData(false)
        }
    }

    const updateFormField = (field: keyof FormData, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }))
    }

    const validateForm = (): boolean => {
        if (!formData.healthPlanId) {
            toast.error('Selecione um convênio')
            return false
        }
        if (!formData.glossReason.trim()) {
            toast.error('Informe o motivo da glosa')
            return false
        }
        if (!formData.originalValue || parseFloat(formData.originalValue) <= 0) {
            toast.error('Informe o valor original')
            return false
        }
        if (!formData.glossedValue || parseFloat(formData.glossedValue) <= 0) {
            toast.error('Informe o valor glosado')
            return false
        }
        return true
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!validateForm()) return
        if (!user?.companyId) {
            toast.error('Usuário não autenticado')
            return
        }

        setLoading(true)
        try {
            if (isEditing && id) {
                const updateData: UpdateGlossData = {
                    type: formData.glossType as any,
                    glossDescription: formData.glossReason,
                    glossValue: parseFloat(formData.glossedValue),
                    notes: formData.notes || undefined,
                }

                const response = await updateGloss(parseInt(id, 10), updateData)
                if (response.success) {
                    toast.success('Glosa atualizada com sucesso!')
                    navigate(`/billing/glosses/${id}`)
                } else {
                    toast.error(response.error || 'Erro ao atualizar glosa')
                }
            } else {
                const createData: CreateGlossData = {
                    healthPlanId: parseInt(formData.healthPlanId, 10),
                    guideId: formData.guideId
                        ? parseInt(formData.guideId, 10)
                        : undefined,
                    itemId: formData.guideItemId
                        ? parseInt(formData.guideItemId, 10)
                        : undefined,
                    glossCode: formData.glossCode || '',
                    glossDescription: formData.glossReason,
                    type: formData.glossType as any,
                    originalValue: parseFloat(formData.originalValue),
                    glossValue: parseFloat(formData.glossedValue),
                    notes: formData.notes || undefined,
                }

                const response = await createGloss(createData)
                if (response.success && response.result) {
                    toast.success('Glosa registrada com sucesso!')
                    navigate(`/billing/glosses/${response.result.id}`)
                } else {
                    toast.error(response.error || 'Erro ao registrar glosa')
                }
            }
        } catch (error) {
            toast.error('Erro ao salvar glosa')
        } finally {
            setLoading(false)
        }
    }

    if (loadingData) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando dados da glosa...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 p-4 max-w-4xl mx-auto">
            <div className="flex items-center gap-3">
                <button
                    onClick={() => navigate('/billing/glosses')}
                    className="btn btn-ghost btn-circle btn-sm"
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-lg">
                        <MdWarning className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">
                            {isEditing ? 'Editar Glosa' : 'Nova Glosa'}
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {isEditing
                                ? 'Atualize as informações da glosa'
                                : 'Registre uma nova glosa'}
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                {/* Basic Info */}
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4">
                            Informações da Glosa
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Convênio{' '}
                                        <span className="text-error">*</span>
                                    </span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={formData.healthPlanId}
                                    onChange={e =>
                                        updateFormField(
                                            'healthPlanId',
                                            e.target.value
                                        )
                                    }
                                    required
                                    disabled={isEditing}
                                >
                                    <option value="">Selecione...</option>
                                    {healthPlans.map(plan => (
                                        <option key={plan.id} value={plan.id}>
                                            {plan.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Tipo de Glosa{' '}
                                        <span className="text-error">*</span>
                                    </span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={formData.glossType}
                                    onChange={e =>
                                        updateFormField(
                                            'glossType',
                                            e.target.value as GlossType
                                        )
                                    }
                                >
                                    {(
                                        Object.keys(
                                            GLOSS_TYPE_LABELS
                                        ) as GlossType[]
                                    ).map(type => (
                                        <option key={type} value={type}>
                                            {GLOSS_TYPE_LABELS[type]}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Número da Guia
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="Número da guia"
                                    value={formData.guideNumber}
                                    onChange={e =>
                                        updateFormField(
                                            'guideNumber',
                                            e.target.value
                                        )
                                    }
                                    disabled={isEditing}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Data da Glosa
                                    </span>
                                </label>
                                <input
                                    type="date"
                                    className="input input-bordered w-full"
                                    value={formData.glossDate}
                                    onChange={e =>
                                        updateFormField(
                                            'glossDate',
                                            e.target.value
                                        )
                                    }
                                    disabled={isEditing}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Order Info */}
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4">
                            Procedimento Glosado
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Código do Procedimento
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="Código TUSS"
                                    value={formData.procedureCode}
                                    onChange={e =>
                                        updateFormField(
                                            'procedureCode',
                                            e.target.value
                                        )
                                    }
                                    disabled={isEditing}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Nome do Procedimento
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="Nome do procedimento"
                                    value={formData.procedureName}
                                    onChange={e =>
                                        updateFormField(
                                            'procedureName',
                                            e.target.value
                                        )
                                    }
                                    disabled={isEditing}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Gloss Details */}
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4">
                            Detalhes da Glosa
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Código da Glosa
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="Código ANS"
                                    value={formData.glossCode}
                                    onChange={e =>
                                        updateFormField(
                                            'glossCode',
                                            e.target.value
                                        )
                                    }
                                />
                            </div>

                            <div className="form-control w-full md:col-span-2">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Motivo da Glosa{' '}
                                        <span className="text-error">*</span>
                                    </span>
                                </label>
                                <textarea
                                    className="textarea textarea-bordered w-full"
                                    placeholder="Descreva o motivo da glosa"
                                    value={formData.glossReason}
                                    onChange={e =>
                                        updateFormField(
                                            'glossReason',
                                            e.target.value
                                        )
                                    }
                                    rows={3}
                                    required
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Valor Original (R$){' '}
                                        <span className="text-error">*</span>
                                    </span>
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    className="input input-bordered w-full"
                                    placeholder="0,00"
                                    value={formData.originalValue}
                                    onChange={e =>
                                        updateFormField(
                                            'originalValue',
                                            e.target.value
                                        )
                                    }
                                    required
                                    disabled={isEditing}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Valor Glosado (R$){' '}
                                        <span className="text-error">*</span>
                                    </span>
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    className="input input-bordered w-full"
                                    placeholder="0,00"
                                    value={formData.glossedValue}
                                    onChange={e =>
                                        updateFormField(
                                            'glossedValue',
                                            e.target.value
                                        )
                                    }
                                    required
                                    disabled={isEditing}
                                />
                            </div>
                        </div>

                        <div className="form-control w-full mt-4">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Observações
                                </span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered w-full"
                                placeholder="Observações adicionais"
                                value={formData.notes}
                                onChange={e =>
                                    updateFormField('notes', e.target.value)
                                }
                                rows={3}
                            />
                        </div>
                    </div>
                </div>

                <div className="flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={() => navigate('/billing/glosses')}
                        className="btn btn-ghost"
                        disabled={loading}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2"
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <span className="loading loading-spinner loading-sm"></span>
                                Salvando...
                            </>
                        ) : (
                            <>
                                <MdSave className="text-lg" />
                                Salvar
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    )
}
